import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { ContentRouter } from '@app/models/content-router';
import {
  AccountService,
  CollectionsService,
  ConfigService,
  FilesService,
  MeetingsBffService,
  UserServicesBffService,
} from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { getMe } from '@store/reducers/profile.reducer';
import { AnyType, ImageSizeEnum, ReviewEntryType, ReviewListItem, User } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCover } from 'onclass-ui';
import { Observable, of } from 'rxjs';
import { map, mergeAll, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-meeting-review',
  templateUrl: './meeting-review.component.html',
  styleUrls: ['./meeting-review.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingReviewComponent extends ChangableComponent implements OnInit {
  private configService = inject(ConfigService);
  private accountService = inject(AccountService);
  private meetingsBffService = inject(MeetingsBffService);
  private userServicesService = inject(UserServicesBffService);
  private collectionsService = inject(CollectionsService);
  private store = inject(Store);
  private router = inject(Router);
  deviceService = inject(DeviceDetectorService);

  @Input() public set meetingReview(meetingReview: ReviewListItem) {
    this._meetingReview = meetingReview;
    this.prepareCard();
  }
  public get meetingReview(): ReviewListItem {
    return this._meetingReview;
  }

  @Output() onEdit = new EventEmitter<boolean>();

  public reviewDate: Date;
  public avatar: OnUiCover;
  public cover: OnUiCover;
  public entryRoute: string;
  public reporterRoute: string;
  public me: User;

  private _meetingReview: ReviewListItem;

  public ngOnInit(): void {
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this.me = me;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected prepareCard() {
    this.reviewDate = new Date(this._meetingReview.updatedAt);
    this.extendReview$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.detectChanges();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        this.detectChanges();
      },
    });
  }

  public edit() {
    this.onEdit.emit(true);
  }

  public get isMeeting() {
    return this.meetingReview.entryType === ReviewEntryType.meeting;
  }

  public goToEntryPage() {
    this.router.navigateByUrl(this.entryRoute).catch((err) => null);
  }

  public get isMe(): boolean {
    return this.me?.id === this._meetingReview?.reporterId;
  }

  private get extendReview$() {
    const observables: Array<Observable<AnyType>> = [this.loadReporter$(this._meetingReview.reporterId)];

    switch (this._meetingReview.entryType) {
      case ReviewEntryType.meeting: {
        observables.push(this.loadMeeting$(this._meetingReview.entryId));
        break;
      }
      case ReviewEntryType.userService: {
        observables.push(this.loadUserService$(this._meetingReview.entryId));
        break;
      }
      case ReviewEntryType.collection: {
        observables.push(this.loadCollection$(this._meetingReview.entryId));
        break;
      }
    }

    return of(...observables).pipe(mergeAll());
  }

  private loadReporter$(id: string) {
    return this.accountService.getUserById(id, 'LOAD_TIME').pipe(
      tap((user) => {
        this.meetingReview.reporterName = AccountService.getUserFullName(user);
        this.reporterRoute = AccountService.accountRoute(user).join('/');
        this.avatar = {
          type: 'avatar',
          img: FilesService.getFileUrlBySize(user?.userpic_id, ImageSizeEnum.sm),
          title: this.meetingReview.reporterName,
          link: this.reporterRoute,
          form: 'circle',
        };
      }),
    );
  }

  private loadMeeting$(id: string) {
    return this.meetingsBffService
      .getOne({ strategy: 'by-id', filter: { id }, details: ['title', 'slug', 'cover_id'] })
      .pipe(
        map((meeting) => {
          this.meetingReview.entryTitle = meeting.title;
          this.entryRoute = ContentRouter.meetingJoinRoute(meeting).join('/');
          this.cover = {
            img: FilesService.getFileUrlBySize(meeting.cover_id, ImageSizeEnum.sm),
            title: this.meetingReview.entryTitle,
          };
        }),
      );
  }

  private loadUserService$(id: string) {
    return this.userServicesService.getOne({ id, details: ['title', 'slug', 'cover_id'] }).pipe(
      map((userService) => {
        this.meetingReview.entryTitle = userService.title;
        this.entryRoute = ContentRouter.userServiceRouteUniversal(userService);
        this.cover = {
          img: FilesService.getFileUrlBySize(userService.cover_id, ImageSizeEnum.sm),
          title: this.meetingReview.entryTitle,
        };
      }),
    );
  }

  private loadCollection$(id: string) {
    return this.collectionsService.getById(id, []).pipe(
      tap((collection) => {
        this.meetingReview.entryTitle = collection.title;
        this.entryRoute = ContentRouter.collectionRouteUniversal(collection);
        this.cover = {
          img: FilesService.getFileUrlBySize(collection.cover_id, ImageSizeEnum.sm),
          title: this.meetingReview.entryTitle,
        };
      }),
    );
  }

  public trackByFn(index) {
    return index;
  }
}
